<template>
  <div
    class="application-period"
    :class="{ 'period-trigger': dateMode }"
    :id="`period-trigger-${_uid}`"
  >
    <Input
      v-if="!dateMode"
      label="Período previsto de aplicação"
      :labelFixed="true"
      type="text"
      :dataInput="dataInput"
      @update:dataInput="updateLegacyText"
    />
    <Input
      v-else
      label="Período previsto de aplicação"
      :labelFixed="true"
      type="text"
      :readonly="true"
      :dataInput="displayValue"
      placeholder="Selecione o período"
      @click.native="calendarOpen = !calendarOpen"
      @keydown.native.enter.prevent="calendarOpen = !calendarOpen"
      @keydown.native.space.prevent="calendarOpen = !calendarOpen"
    />
    <b-icon-calendar3 v-if="dateMode" class="period-icon" aria-hidden="true" />
    <b-popover
      :id="`period-calendar-${_uid}`"
      :target="`period-trigger-${_uid}`"
      :show.sync="calendarOpen"
      triggers="manual"
      placement="bottom"
      no-fade
      custom-class="period-calendar-popover"
    >
      <div class="calendar-prompt">{{ awaitingEnd ? 'Selecione a data final' : 'Selecione a data inicial' }}</div>
      <b-calendar
        locale="pt-BR"
        label-help=""
        :value="awaitingEnd ? startDate : (endDate || startDate)"
        :date-info-fn="dateInfo"
        width="260px"
        @selected="selectDate"
      />
      <button type="button" class="clear-period" @click="clearPeriod">Limpar período</button>
    </b-popover>
  </div>
</template>

<script>
import Input from "../UI/CustomInputs/Input.vue";

const validDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

const toISO = (date) => `${date.slice(6, 10)}-${date.slice(3, 5)}-${date.slice(0, 2)}`;
const toDisplay = (date) => `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`;

export default {
  components: { Input },
  props: {
    dataInput: { type: [String, Number], default: "" },
  },
  data() {
    return { dateMode: true, startDate: "", endDate: "", awaitingEnd: false, calendarOpen: false, lastEmitted: null };
  },
  computed: {
    // Compatível com a restauração de campos dos modelos predefinidos.
    valueInput: {
      get() { return this.dataInput; },
      set(value) { this.syncValue(value); },
    },
    displayValue() {
      return this.awaitingEnd ? `${toDisplay(this.startDate)} — Selecione a data final` : this.dataInput;
    },
  },
  created() {
    this.syncValue(this.dataInput);
  },
  mounted() {
    document.addEventListener("mousedown", this.closeOutside);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.closeOutside);
  },
  watch: {
    dataInput(value) {
      if (value === this.lastEmitted) {
        this.lastEmitted = null;
        return;
      }
      this.syncValue(value);
    },
    calendarOpen(open) {
      if (!open && this.awaitingEnd) this.syncValue(this.dataInput);
    },
  },
  methods: {
    syncValue(value) {
      // Fecha o calendário antes de alternar entre o campo de datas e o texto legado.
      this.calendarOpen = false;
      const match = typeof value === "string" && value.match(/^(\d{2}\/\d{2}\/\d{4}) a (\d{2}\/\d{2}\/\d{4})$/);
      if (match && validDate(toISO(match[1])) && validDate(toISO(match[2])) && toISO(match[1]) <= toISO(match[2])) {
        this.dateMode = true;
        this.startDate = toISO(match[1]);
        this.endDate = toISO(match[2]);
      } else {
        this.dateMode = !value;
        this.startDate = "";
        this.endDate = "";
      }
      this.awaitingEnd = false;
    },
    emitValue(value) {
      this.lastEmitted = value;
      this.$emit("update:dataInput", value);
    },
    updateLegacyText(value) {
      if (!value) this.dateMode = true;
      this.emitValue(value);
    },
    selectDate(date) {
      if (!validDate(date)) return;
      if (!this.awaitingEnd || date < this.startDate) {
        this.startDate = date;
        this.endDate = "";
        this.awaitingEnd = true;
        return;
      }
      this.endDate = date;
      this.awaitingEnd = false;
      this.emitValue(`${toDisplay(this.startDate)} a ${toDisplay(this.endDate)}`);
      this.calendarOpen = false;
    },
    dateInfo(date) {
      return this.startDate && this.endDate && date >= this.startDate && date <= this.endDate
        ? 'period-in-range' : '';
    },
    clearPeriod() {
      this.startDate = "";
      this.endDate = "";
      this.awaitingEnd = false;
      this.emitValue("");
    },
    closeOutside(event) {
      if (!this.calendarOpen) return;
      const popover = document.getElementById(`period-calendar-${this._uid}`);
      if (!this.$el.contains(event.target) && !popover?.contains(event.target)) {
        this.calendarOpen = false;
      }
    },
  },
};
</script>

<style>
.application-period { width: 100%; min-width: 0; }
.period-trigger { position: relative; }
.period-trigger .input { cursor: pointer; padding-right: 28px; }
.period-icon { position: absolute; right: 9px; top: 8px; color: #006256; pointer-events: none; }
.period-calendar-popover { max-width: calc(100vw - 16px); }
.period-calendar-popover .popover-body { padding: 8px; }
.calendar-prompt { color: #006256; font-size: 12px; font-weight: 600; margin-bottom: 5px; }
.clear-period { border: 0; background: none; color: #006256; font-size: 12px; padding: 4px 0 0; cursor: pointer; }
.period-calendar-popover .period-in-range .btn { background-color: #d8eee5; border-radius: 0; }
.period-calendar-popover .b-calendar-grid-help { display: none; }
.period-calendar-popover .b-calendar-grid-caption { font-size: 12px; }
.period-calendar-popover .b-calendar-grid-weekdays small { font-size: 11px; }
.period-calendar-popover .b-calendar-grid-body .btn { font-size: 12px; }
</style>
